/**
 * What the "Recently opened" list says when it has no rows to show (#1790).
 *
 * SLM-MOONBASE #124's incident report lists three symptoms. Two panels that
 * said nothing useful, and *"cached recent-record UUIDs"*. The uuids are gone —
 * #1705/TEC-02 replaced the id fallback with a governed title read and filters
 * a ref out entirely when it cannot be resolved, so no id is ever rendered
 * where a name goes.
 *
 * But that filter has a cost nobody had looked at. When the data service is not
 * answering, NOTHING resolves, every row is filtered out, and the list falls
 * through to its empty state — which said:
 *
 *     Open a few records — they queue up here.
 *
 * They have. The recents are still there and the list is **unreadable, not
 * empty**. Inviting someone to create the thing they already have is the same
 * false claim of health as the uuids were, one step further along: the surface
 * looks fine and quietly misdescribes the deployment.
 *
 * (This sentence said "recents are device-local". Since #2325 they are per-user
 * and server-backed at `core.user_setting['recents']` — which STRENGTHENS the
 * case rather than weakening it: the list survives the browser now, so "nothing
 * has been lost" is true of the other laptop too, and an empty render is even
 * further from the truth than it was when the bytes were on this machine.)
 *
 * This is a separate module from `ShellDrawer.tsx` for one reason: the decision
 * is three booleans and a sentence, and it is worth being able to state the
 * cases without mounting a drawer that needs a router, a query client and a
 * pinned store to exist.
 */

export type RecentsEmptyState =
  | { kind: 'none'; text: string }
  | { kind: 'unnameable'; text: string }
  | null

/**
 * @param stored  recents held on this device, after pinned ones are removed
 * @param shown   how many of those could actually be named
 * @param pending whether the governed title read is still in flight
 */
export function recentsEmptyState(
  { stored, shown, pending }: { stored: number; shown: number; pending: boolean },
): RecentsEmptyState {
  if (shown > 0) return null
  /* Still loading is not a claim. Saying "nothing has been lost" here would
     flash a failure message during every cold open of the drawer. */
  if (pending) return null
  if (stored > 0) {
    return {
      kind: 'unnameable',
      /* Deliberately does NOT name the failing service. The app-level region
         (#1790) says which dependency is down, once, above everything. This row
         owes only the truth about ITSELF — and "nothing has been lost" is the
         part a person actually needs, because the alternative reading of an
         empty list is that their history was thrown away. */
      text: 'Your recent records can’t be shown right now. Nothing has been lost.',
    }
  }
  return { kind: 'none', text: 'Open a few records — they queue up here.' }
}
